import React, { FC, memo, useEffect, useState } from 'react';
import { getActions, withGlobal } from '../../global';
import type { GlobalState } from '../../global/types';
import { Helmet } from 'react-helmet';
import AuthUserBlocked from './AuthUserBlocked';
import { GOOGLE_CAPTCHA, GOOGLE_CLIENT_ID } from '../../config';
import { ConfirmEmailType, ERoutes } from '../../types';
import AuthSignIn from './AuthSignIn';
import { useParams } from 'react-router';
import useLastCallback from '../../hooks/useLastCallback';
import Transition from '../ui/Transition';
import CreateAccountForm from './CreateAccountForm';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { parseInitialLocationHash } from '../../util/routing';
import { processDeepLink } from '../../util/deeplink';

import './Auth.scss';

type StateProps = {
  confirmType?: ConfirmEmailType;
  nonce?: string;
} & Pick<GlobalState, 'authRefCode' | 'connectionState'>;

const Auth: FC<StateProps> = ({ connectionState, nonce }) => {
  const { requestAuthNonce } = getActions();
  const { tab } = useParams();

  const [renderingKey, setRenderingKey] = useState<number>(0);

  const renderContent = useLastCallback(() => {
    switch (tab) {
      case ERoutes.createAccount:
        return <CreateAccountForm />;
      case ERoutes.blocked:
        return <AuthUserBlocked />;
      default:
        return <AuthSignIn />;
    }
  });

  useEffect(() => {
    switch (tab) {
      case ERoutes.createAccount:
        setRenderingKey(1);
        break;
      case ERoutes.blocked:
        setRenderingKey(2);
        break;
      default:
        setRenderingKey(0);
    }
  }, [tab]);

  // Parse deep link
  useEffect(() => {
    const parsedInitialLocationHash = parseInitialLocationHash();
    console.log('parsedInitialLocationHash', parsedInitialLocationHash);
    if (parsedInitialLocationHash?.elloaddr) {
      processDeepLink(decodeURIComponent(parsedInitialLocationHash.elloaddr));
    }
  }, []);

  useEffect(() => {
    if (connectionState === 'connectionStateReady') {
      requestAuthNonce();
    }
  }, [connectionState]);

  return (
    <>
      <Helmet
        title={`${
          tab === ERoutes.signUp
            ? 'Sign Up'
            : tab === ERoutes.signIn
            ? 'Sign In'
            : ''
        }`}
      />

      <GoogleReCaptchaProvider language="en" reCaptchaKey={GOOGLE_CAPTCHA || ''}>
        <Transition
          className='transition-block'
          name='none'
          activeKey={renderingKey}
          shouldCleanup
        >
          {renderContent()}
        </Transition>
      </GoogleReCaptchaProvider>
    </>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { confirmData, authRefCode, connectionState, twoFaSettings } = global;
    return {
      authRefCode,
      confirmType: confirmData?.type,
      connectionState,
      nonce: twoFaSettings.nonce,
    };
  })(Auth)
);
